import React, { KeyboardEvent } from 'react';
import { Input, IconButton, useStyles2 } from '@grafana/ui';
import { css, cx, keyframes } from '@emotion/css';
import { GrafanaTheme2 } from '@grafana/data';
import { useInlineAssistant } from '../../inline/useInlineAssistant';
import { createBorderAnimation, getBackgroundGradientColors } from '../../utils/theme';
import { AIInputProps } from './types';

export interface AITextInputProps extends AIInputProps {}

/**
 * An AI-powered controlled text input component that uses the Grafana Assistant engine to generate content.
 * Features an integrated send button as well as a gradient border animation while generating.
 */
export function AITextInput({
  value,
  onChange,
  placeholder,
  origin,
  systemPrompt,
  onComplete,
  onError,
  onDelta,
  disabled = false,
  className,
  'data-testid': dataTestId,
}: AITextInputProps) {
  const { generate, isGenerating, error, reset } = useInlineAssistant();

  // Determine if send button should be disabled
  const isSendDisabled = !value.trim() || disabled || isGenerating;
  const styles = useStyles2(getStyles);

  const handleGenerate = async () => {
    if (isSendDisabled) {
      return;
    }

    const prompt = value.trim();
    reset(); // Clear previous content/error

    await generate({
      prompt,
      origin,
      systemPrompt,
      onComplete: (text) => {
        onComplete?.(text);
      },
      onError: (err) => {
        onError?.(err);
      },
      onDelta: (delta) => {
        onDelta?.(delta);
      },
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Cmd/Ctrl + Enter to send
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const addonAfter = (
    <IconButton
      name="ai-sparkle"
      onClick={handleGenerate}
      disabled={isSendDisabled}
      tooltip={isSendDisabled ? 'Enter text to generate' : 'Generate with AI'}
      data-testid={dataTestId ? `${dataTestId}-send` : undefined}
    />
  );

  return (
    <div className={cx(styles.wrapper, { [styles.animatedWrapper]: isGenerating }, className)} data-testid={dataTestId}>
      <Input
        value={value}
        onKeyDown={handleKeyDown}
        onChange={(e) => onChange(e.currentTarget.value)}
        placeholder={placeholder ?? 'Ask AI to generate...'}
        disabled={disabled || isGenerating}
        suffix={addonAfter}
        invalid={!!error}
        data-testid={dataTestId ? `${dataTestId}-input` : undefined}
      />
    </div>
  );
}

const getStyles = (theme: GrafanaTheme2) => {
  const backgroundColor = theme.colors.background.primary;
  const borderAnimation = keyframes({
    label: 'ai-input-border-animation',
    ...createBorderAnimation(theme, backgroundColor),
  });

  const [gradientColor1, gradientColor2] = getBackgroundGradientColors(theme);

  return {
    wrapper: css({
      label: 'ai-text-input-wrapper',
      position: 'relative',
      borderRadius: theme.shape.radius.default,
    }),
    animatedWrapper: css({
      label: 'ai-text-input-animated',
      border: `1px solid transparent`,
      // Set initial backgroundImage with both gradients so Chrome can animate properly
      backgroundImage: `
        linear-gradient(${backgroundColor}, ${backgroundColor}),
        conic-gradient(from 0deg, transparent 60%, ${gradientColor1} 80%, ${gradientColor2} 100%, transparent 15%)
      `,
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      animation: `${borderAnimation} 2s linear infinite`,
      '& input': {
        // Remove the default border from the input when animated wrapper is active
        border: 'none !important',
      },
    }),
  };
};
